import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Button, TextField, Typography, Chip,
} from '@mui/material';
import { Add, Refresh, ReceiptLong, AttachMoney } from '@mui/icons-material';
import {
  PageHeader, StatCard, FilterBar, FilterSpacer, TableSurface, headerCellSx, bodyRowSx,
  TableEmptyState, ErrorState,
} from '../../components/common/pageKit';
import ExportButton from '../../components/common/ExportButton';
import { collectionsService, CustomerReceipt } from '../../services/collectionsService';
import { formatIndianNumber } from '../../utils/currency';

const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const CustomerPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CustomerReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    collectionsService.listReceipts()
      .then((r) => { setRows(r.receipts || []); setError(null); })
      .catch((e: any) => setError(e?.message || 'Failed to load customer payments'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.receiptNumber, r.customerName, r.invoiceNumber, r.reference].some((v) => (v || '').toLowerCase().includes(q));
  }), [rows, search]);

  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Customer Payments"
        subtitle="Receipts recorded against customer invoices — every row posts the balanced Dr Bank / Cr Accounts Receivable entry."
        actions={(
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ mr: 1 }}>Refresh</Button>
            <ExportButton
              rows={filtered as any}
              columns={[
                { header: 'Receipt #', key: 'receiptNumber' }, { header: 'Date', key: 'paymentDate' },
                { header: 'Customer', key: 'customerName' }, { header: 'Invoice', key: 'invoiceNumber' },
                { header: 'Amount', key: 'amount' }, { header: 'Mode', key: 'paymentMode' }, { header: 'Reference', key: 'reference' },
              ]}
              filename="customer-payments"
            />
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/finance/customer-payments/new')}>
              New Receipt
            </Button>
          </>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3, maxWidth: 560 }}>
        <StatCard title="Total Receipts" value={loading ? null : rows.length} icon={<ReceiptLong />} color="#2563eb" />
        <StatCard title="Total Collected" value={loading ? null : formatIndianNumber(totalAmount)} icon={<AttachMoney />} color="#16a34a" />
      </Box>

      <FilterBar>
        <TextField size="small" placeholder="Search receipt / customer / invoice / reference" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 320 }} />
        <FilterSpacer />
        <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length}</Typography>
      </FilterBar>

      {error ? <ErrorState message={error} onRetry={load} /> : (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Receipt #', 'Date', 'Customer', 'Invoice', 'Amount', 'Mode', 'Reference'].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
                ) : !filtered.length ? (
                  <TableRow><TableCell colSpan={7} sx={{ border: 0 }}><TableEmptyState title="No customer payments" hint="Record a receipt against an open invoice to see it here." /></TableCell></TableRow>
                ) : filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    onClick={() => navigate(`/finance/customer-payments/${r.id}`)}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                  >
                    <TableCell><Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.receiptNumber}</Typography></TableCell>
                    <TableCell>{fmtDate(r.paymentDate)}</TableCell>
                    <TableCell>{r.customerName}</TableCell>
                    <TableCell>{r.invoiceNumber}</TableCell>
                    <TableCell><Typography sx={{ fontWeight: 700 }}>{formatIndianNumber(Number(r.amount || 0))}</Typography></TableCell>
                    <TableCell><Chip label={(r.paymentMode || '').replace('_', ' ')} size="small" variant="outlined" /></TableCell>
                    <TableCell>{r.reference || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}
    </Box>
  );
};

export default CustomerPaymentPage;
